/**
 * `/setup`'s Connect your AI card (slice 1b spec §6.2): the providers, a key
 * or a sign-in (ChatGPT's or Grok's), and the model; then the connect's stages, live
 * (`ai.stage`), until it's connected, or why it stopped with Try again.
 * Settings → AI has its own panel (`ConnectPanel`), built from the same parts.
 */
import { Button, buttonVariants } from "@crew/ui/components/button";
import { FieldError } from "@crew/ui/components/field";
import { ArrowRight, RotateCw } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { AiProvider, Me } from "../../app/me";
import { useModels } from "./api";
import { Checklist, FailureBox, SetupCardFooter } from "./checklist";
import {
  ApiKeyField,
  CancelSignIn,
  ClaudeTokenFields,
  ModelField,
  SignInCode,
  SignInWaiting,
  WorkersAiFields,
} from "./connect-panel";
import type { Connect, SetupConnectState } from "./hooks";
import {
  connectAccount,
  connectBody,
  connectErrors,
  connectLabel,
  errorParts,
  formModel,
  modelLabel,
  connectModel,
  connectPicksDefault,
  providerModels,
  PROVIDERS,
  signsIn,
} from "./onboarding";
import {
  formNote,
  formPick,
  formState,
  progressNote,
  stageRows,
  summaryTitle,
  type SetupView,
} from "./setup-view";
import { ProviderLogo, ProviderRadioGrid } from "./provider-tiles";
import { HOME } from "../../app/home";

type Ai = NonNullable<Me["computer"]>["ai"];

/** The launch providers, in the grid's order. */
const PROVIDER_ORDER = Object.keys(PROVIDERS) as AiProvider[];

/** The model a connect is for: the one sent, else the workspace's when it's this provider's. */
function useModelLabel(connect: Connect, ai: Ai, provider: AiProvider): string | null {
  const models = useModels();
  const model = connectModel(
    connect.variables?.model,
    ai.model,
    providerModels(models.data, provider),
  );
  return modelLabel(models.data, model);
}

/** A sign-in's Try again: the model it was sent with, if this connect picks the default. */
function retryModel(ai: Ai, connect: Connect): string | undefined {
  return connectPicksDefault(ai) ? connect.variables?.model : undefined;
}

/** The card's body, by where the connect is. */
export function SetupConnect({ ai, state }: { ai: Ai; state: SetupConnectState }) {
  if (state.view === "form" || state.view === "sign-in")
    return <ConnectForm ai={ai} state={state} />;
  return <ConnectProgress ai={ai} state={state} />;
}

/** The providers, the key or the sign-in, the model, and Connect. */
function ConnectForm({ ai, state }: { ai: Ai; state: SetupConnectState }) {
  const { connect, provider, view } = state;
  const signingIn = view === "sign-in";
  const models = useModels();
  const [picked, setPicked] = useState<string>();
  const [apiKey, setApiKey] = useState("");
  const [email, setEmail] = useState("");
  // Workers AI's Cloudflare account (slice 1h): the first connect needs one.
  const [accountId, setAccountId] = useState("");
  const sent = connect.variables?.model ?? ai.model;
  // Only the first connection picks the default (slice 1c spec §3.4): after
  // a new computer, the default stays whichever AI is connected here.
  const picks = connectPicksDefault(ai);
  const model = formModel(
    picks,
    formPick(signingIn, picked, sent),
    providerModels(models.data, provider),
    null,
  );
  const choose = (p: AiProvider) => {
    setPicked(undefined);
    setApiKey("");
    state.choose(p);
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!provider) return;
    connect.mutate(connectBody(provider, model, apiKey, undefined, email, accountId), {
      onSuccess: () => setApiKey(""),
    });
  };
  return (
    <form
      onSubmit={submit}
      data-connect-state={formState(signingIn, ai.pending)}
      className="flex flex-col"
    >
      <div className="flex flex-col gap-2.5 px-5.5 pt-3.5">
        <p className="text-meta leading-4.5 font-medium text-muted-foreground">
          Which AI do you use?
        </p>
        <ProviderRadioGrid
          providers={PROVIDER_ORDER.filter((p) => !ai.providers_off?.includes(p))}
          value={provider}
          onChange={choose}
          locked={signingIn}
        />
      </div>
      <CredentialFields
        provider={provider}
        apiKey={apiKey}
        onKey={setApiKey}
        email={email}
        onEmail={setEmail}
        accountId={accountId}
        onAccountId={setAccountId}
        keyError={connectErrors(connect.error).key}
        model={model}
        onModel={setPicked}
        picksModel={picks}
        keptModel={ai.model}
        signingIn={signingIn}
      />
      <SignInSection show={signingIn} provider={provider} pending={ai.pending} />
      <FormError error={connect.error} />
      <SetupCardFooter note={formNote(provider, signingIn)}>
        <FormAction ai={ai} state={state} />
      </SetupCardFooter>
    </form>
  );
}

/** The provider's credential: Claude's token, Workers AI's account and token, or a key. */
function Credential({
  provider,
  apiKey,
  onKey,
  email,
  onEmail,
  accountId,
  onAccountId,
  keyError,
}: {
  provider: AiProvider;
  apiKey: string;
  onKey: (key: string) => void;
  email: string;
  onEmail: (email: string) => void;
  accountId: string;
  onAccountId: (id: string) => void;
  keyError: string | null;
}) {
  if (signsIn(provider)) return null;
  const fields =
    provider === "claude" ? (
      <ClaudeTokenFields
        token={apiKey}
        onToken={onKey}
        email={email}
        onEmail={onEmail}
        error={keyError}
      />
    ) : provider === "workers-ai" ? (
      <WorkersAiFields
        accountId={accountId}
        onAccountId={onAccountId}
        token={apiKey}
        onToken={onKey}
        error={keyError}
      />
    ) : (
      <ApiKeyField provider={provider} value={apiKey} onChange={onKey} error={keyError} />
    );
  return <div className="px-5.5 pt-5.5">{fields}</div>;
}

/** A key provider's key, then the model; nothing before a provider is picked. */
function CredentialFields({
  provider,
  apiKey,
  onKey,
  email,
  onEmail,
  accountId,
  onAccountId,
  keyError,
  model,
  onModel,
  picksModel,
  keptModel,
  signingIn,
}: {
  provider: AiProvider | null;
  apiKey: string;
  onKey: (key: string) => void;
  email: string;
  onEmail: (email: string) => void;
  accountId: string;
  onAccountId: (id: string) => void;
  keyError: string | null;
  model: string | undefined;
  onModel: (model: string) => void;
  /** Whether this connect picks the default model: else the default it keeps. */
  picksModel: boolean;
  keptModel: string | null;
  signingIn: boolean;
}) {
  if (!provider) return <div className="pb-5.5" />;
  return (
    <>
      <Credential
        provider={provider}
        apiKey={apiKey}
        onKey={onKey}
        email={email}
        onEmail={onEmail}
        accountId={accountId}
        onAccountId={onAccountId}
        keyError={keyError}
      />
      <div className="px-5.5 pt-5 pb-5.5">
        <ModelField
          show={picksModel}
          kept={keptModel}
          provider={provider}
          value={model}
          onChange={onModel}
          disabled={signingIn}
        />
      </div>
    </>
  );
}

/** The sign-in's code (ChatGPT's or Grok's), and the wait for approval once it shows. */
function SignInSection({
  show,
  provider,
  pending,
}: {
  show: boolean;
  provider: AiProvider | null;
  pending: Ai["pending"];
}) {
  if (!show || !signsIn(provider)) return null;
  return (
    <div className="flex flex-col gap-3.5 px-5.5 pb-5.5">
      <SignInCode provider={provider} pending={pending} />
      {pending ? <SignInWaiting provider={provider} pending={pending} /> : null}
    </div>
  );
}

/** Anything refused but a bad key (that shows under the key field). */
function FormError({ error }: { error: unknown }) {
  const other = connectErrors(error).other;
  return other ? <FieldError className="px-5.5 pb-4">{other}</FieldError> : null;
}

/** Connect (Sign in with ChatGPT or Grok, Try again), or Cancel sign-in once the code shows. */
function FormAction({ ai, state }: { ai: Ai; state: SetupConnectState }) {
  const { connect, provider, view } = state;
  if (view === "sign-in") {
    return ai.pending ? <CancelSignIn label="Cancel sign-in" variant="outline" /> : null;
  }
  if (!provider) return null;
  const label = connectLabel(provider, {
    pending: connect.isPending,
    retrying: ai.status === "failed",
  });
  return (
    <Button type="submit" disabled={connect.isPending}>
      {label}
    </Button>
  );
}

/** Which account, on which model: the card's summary once a connect is under way. */
function AccountSummary({
  ai,
  provider,
  connect,
}: {
  ai: Ai;
  provider: AiProvider;
  connect: Connect;
}) {
  const model = useModelLabel(connect, ai, provider);
  const label = connectAccount(ai)?.label;
  return (
    <div className="flex items-center gap-3 rounded-lg bg-foreground/3 px-3.5 py-3">
      <ProviderLogo provider={provider} />
      <div className="flex min-w-0 grow flex-col gap-px">
        <p className="text-sm leading-4.5 font-semibold text-foreground">
          {summaryTitle(provider, model)}
        </p>
        {label ? <p className="text-meta leading-4.5 text-muted-foreground">{label}</p> : null}
      </div>
    </div>
  );
}

/** Connecting, stopped or connected: the stages, and what to do next. */
function ConnectProgress({ ai, state }: { ai: Ai; state: SetupConnectState }) {
  const { view, provider } = state;
  const rows = stageRows(view, ai, provider);
  return (
    <div className="flex flex-col" data-connect-state={view}>
      <div className="flex flex-col gap-3.5 px-5.5 pt-3 pb-3.5">
        {provider ? <AccountSummary ai={ai} provider={provider} connect={state.connect} /> : null}
        <Checklist
          kind="stage"
          rows={rows}
          failure={view === "failed" ? <ConnectFailed ai={ai} state={state} /> : undefined}
        />
      </div>
      <ProgressFooter view={view} />
    </div>
  );
}

/** Why the connect stopped, Try again, and the way back to the providers. */
function ConnectFailed({ ai, state }: { ai: Ai; state: SetupConnectState }) {
  const { connect, provider } = state;
  const parts = errorParts(state.failure ?? "Connecting didn't finish.");
  // A sign-in starts again at once; a key is asked for again (it isn't kept).
  const again = () => {
    if (signsIn(provider)) connect.mutate(connectBody(provider, retryModel(ai, connect), ""));
    else state.edit(provider);
  };
  return (
    <>
      <FailureBox {...parts} />
      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={again} disabled={connect.isPending}>
          <RotateCw aria-hidden />
          Try again
        </Button>
        <Button
          variant="link"
          className="h-auto p-0 text-meta text-foreground underline decoration-foreground/30 underline-offset-[3px]"
          onClick={() => state.edit(null)}
        >
          Or pick a different AI
        </Button>
      </div>
      {connect.error ? <FieldError>{connectErrors(connect.error).other}</FieldError> : null}
    </>
  );
}

/** How long Done waits before it goes to Chats on its own. */
const DONE_PAUSE_MS = 2_500;

function ProgressFooter({ view }: { view: SetupView }) {
  const navigate = useNavigate();
  useEffect(() => {
    if (view !== "done") return;
    const go = setTimeout(() => navigate(HOME, { replace: true }), DONE_PAUSE_MS);
    return () => clearTimeout(go);
  }, [view, navigate]);
  if (view === "done") {
    return (
      <SetupCardFooter note="Taking you to Chats…">
        <Link to={HOME} replace className={buttonVariants()}>
          Go to Chats
          <ArrowRight aria-hidden />
        </Link>
      </SetupCardFooter>
    );
  }
  return <SetupCardFooter note={progressNote(view)} />;
}
